import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Play } from 'lucide-react';
import { useHeroImage } from '@/hooks/useHeroImage';
import HeroImageEditor from '@/components/HeroImageEditor';
import PageIcon from '@/components/PageIcon';
import PageQuote from '@/components/PageQuote';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PatientVideos() {
  const [videos, setVideos] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const heroImage = useHeroImage('patient_videos');

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to load videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: 'All Videos' },
    { value: 'nutrition', label: 'Nutrition' },
    { value: 'qigong', label: 'Qi Gong' },
    { value: 'daily_life', label: 'Daily Life Advice' }
  ];

  const filteredVideos = selectedCategory === 'all'
    ? videos
    : videos.filter(v => v.category === selectedCategory);

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = url.split('v=')[1] || url.split('/').pop();
    return `https://www.youtube.com/embed/${videoId}`;
  };

  return (
    <Layout>
      <section className="min-h-screen" data-testid="patient-videos-page">
        {/* Compact Hero with Colorful Gradient */}
        <div className="relative h-[400px] overflow-hidden">
          <HeroImageEditor pageName="patient_videos" />
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{
              backgroundImage: `url(${heroImage})`,
              filter: 'sepia(40%) saturate(120%) hue-rotate(-5deg) brightness(0.9)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-br from-[#E85D4D]/60 via-[#E8934D]/40 to-[#4D9E7C]/60"></div>
          
          {/* Text Overlay */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
            <PageIcon pageName="patient_videos" size="large" />
            <h1 className="text-5xl md:text-7xl retro-title mb-4 text-center" data-testid="page-title">Patient Resources</h1>
            <p className="text-2xl md:text-3xl text-[#F4E4C1] font-bold mb-2 text-center drop-shadow-lg">Wellness Videos for Your Journey</p>
            <p className="text-lg md:text-xl text-[#F4E4C1] font-bold text-center drop-shadow-lg">Serving the Chicago Community</p>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          {/* Quote Position 1 - Above Main Content */}
          <PageQuote pageName="patient_videos" position={1} variant="compact" />

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="category-filter">
            {categories.map(cat => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`retro-button ${
                  selectedCategory === cat.value
                    ? 'bg-[#6B4423] text-[#F4E4C1]'
                    : ''
                }`}
                data-testid={`category-${cat.value}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center" data-testid="loading-state">
              <div className="w-16 h-16 border-4 border-[#D4A574] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : filteredVideos.length === 0 ? (
            <div className="text-center cassette-card p-12 max-w-2xl mx-auto" data-testid="no-videos">
              <p className="text-[#3D2817] text-xl">No videos in this category yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredVideos.map((video) => (
                <div key={video.id} className="video-card group" data-testid={`video-card-${video.id}`}>
                  <div className="aspect-video bg-[#2A1810] relative overflow-hidden">
                    {video.youtube_url ? (
                      <iframe
                        src={getYoutubeEmbedUrl(video.youtube_url)}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        data-testid={`video-iframe-${video.id}`}
                      ></iframe>
                    ) : video.file_path ? (
                      <video
                        controls
                        className="w-full h-full"
                        data-testid={`video-player-${video.id}`}
                      >
                        <source src={`${BACKEND_URL}${video.file_path}`} type="video/mp4" />
                      </video>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <Play className="w-16 h-16 text-[#D4A574]" />
                      </div>
                    )}
                    {video.thumbnail_url && !video.youtube_url && !video.file_path && (
                      <img
                        src={video.thumbnail_url}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="p-4 bg-[#D4A574]">
                    <h3 className="text-xl font-bold text-[#3D2817] mb-2" data-testid={`video-title-${video.id}`}>{video.title}</h3>
                    <p className="text-[#3D2817] text-sm" data-testid={`video-description-${video.id}`}>{video.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Quote Position 2 - Below Main Content */}
      <PageQuote pageName="patient_videos" position={2} variant="compact" />
    </Layout>
  );
}